//@flow
import * as React from 'react';
import {
    Dialog,
    DialogHeader,
    DialogContent,
    DialogFooter,
    Heading,
    Button,
    Text
} from '@locus-taxy/neo-ui';
import { useTranslation } from 'react-i18next';
import PjpActions from 'common/redux/actions/pjpMaster';
import FpPlanViewActions from 'common/redux/actions/FpPlanViewActions';
import IncrementalPjpStepsContainer from './IncrementalPjpStepsContainer';
import theme from './Incremental.scss';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Loader } from 'common/components/core/loader/Loader';
import {
    PjpPlanStatus,
    AllowedStatuses
} from 'common/models/internal/PermanentJourneyPlan';
import {
    CreateIncrementalPjpRequest,
    EntityIds
} from 'common/models/internal/CreateIncrementalPjpRequest';
import IncrementalConfigSelector, {
    INCREMENTAL_PLAN_CONFIG_TYPE
} from './IncrementalConfigSelector';
import IncrementalPlanProvider from './IncrementalPjpContext';
import {
    AccessDeniedDialog,
    usePermissionMatch,
    RoleOperationEnum
} from 'common/modules/rbac';

type Props = {
    history: Object,
    match: Object,
    onDismiss: (reload?: boolean) => void
};

const incrementalStepsPlanConfig = Object.freeze({
    [INCREMENTAL_PLAN_CONFIG_TYPE.RETAIN_NO_BEATS]: {
        newOutletsSelection: true,
        oldOutletsSelection: true,
        newRidersSelection: true,
        oldRidersSelection: true,
        forceReplan: true
    },
    [INCREMENTAL_PLAN_CONFIG_TYPE.RETAIN_ALL_BEATS]: {
        newOutletsSelection: true,
        oldOutletsSelection: false,
        newRidersSelection: true,
        oldRidersSelection: false,
        forceReplan: false
    },
    [INCREMENTAL_PLAN_CONFIG_TYPE.RETAIN_SOME_BEATS]: {
        newOutletsSelection: true,
        oldOutletsSelection: true,
        newRidersSelection: true,
        oldRidersSelection: true,
        forceReplan: false
    }
});

const IncrementalPjpPlan = (props: Props): React.Node => {
    const [incrementalPlanType, setIncrementalPlanType] = React.useState(null);
    const [incrementalPlanConfig, setIncrementalPlanConfig] = React.useState(
        null
    );
    const [selectedPlan, setSelectedPlan] = React.useState();

    const { t } = useTranslation('entityManagement');

    const { onDismiss } = props;

    const onDismissPlan = React.useCallback(() => {
        onDismiss(false);
    }, [onDismiss]);
    const selectFinalPlanType = React.useCallback(planType => {
        setIncrementalPlanType(planType);
    }, []);

    const { pjpId, clientId } = useParams();

    React.useEffect(() => {
        if (!selectedPlan) {
            FpPlanViewActions.fetchFpPjPlanCall({ clientId, pjpId })
                .then(plan => {
                    setSelectedPlan({
                        ...plan,
                        getEffectiveStatus() {
                            if (AllowedStatuses.includes(this.planStatus)) {
                                if (this.planStatus === PjpPlanStatus.Frozen) {
                                    return PjpPlanStatus.Finalised;
                                }
                                return this.planStatus;
                            }
                            return PjpPlanStatus.Planning;
                        }
                    });
                })
                .catch(error => {
                    throw error;
                });
        }
    }, [clientId, pjpId, selectedPlan]);

    const config = incrementalPlanType
        ? incrementalStepsPlanConfig[incrementalPlanType]
        : {};

    const dispatch = useDispatch();

    const _getSelectedEntitiesByPlanConfig = React.useCallback(
        (newEntities, existingEntities) => {
            if (config.forceReplan) {
                return existingEntities.concat(newEntities);
            }
            return newEntities;
        },
        [config]
    );

    const onOutletsNext = React.useCallback(
        ({ existingSelectedRows, newSelectedRows }) => {
            setIncrementalPlanConfig(
                CreateIncrementalPjpRequest.fromJS({
                    outletsData: {
                        ids: _getSelectedEntitiesByPlanConfig(
                            newSelectedRows,
                            existingSelectedRows
                        )
                    },
                    ridersData: {
                        ids: []
                    },
                    forceReplan: config.forceReplan
                })
            );
        },
        [_getSelectedEntitiesByPlanConfig, config.forceReplan]
    );

    const onRidersNext = React.useCallback(
        ({ existingSelectedRows, newSelectedRows }) => {
            setIncrementalPlanConfig(configData => {
                return configData?.setRidersData(
                    EntityIds.fromJS({
                        ids: _getSelectedEntitiesByPlanConfig(
                            newSelectedRows,
                            existingSelectedRows
                        )
                    })
                );
            });
        },
        [_getSelectedEntitiesByPlanConfig]
    );

    const onPlanCreate = React.useCallback(
        options => {
            dispatch(
                PjpActions.createPjpIncrementalPlan(
                    {
                        pjpId,
                        clientId
                    },
                    incrementalPlanConfig,
                    options
                )
            );
        },
        [clientId, dispatch, incrementalPlanConfig, pjpId]
    );

    if (!selectedPlan) {
        // pjp plans are not loaded - pjpSearch api
        return <Loader active className={theme.tableLoader} />;
    }

    const pjPlanStatus = selectedPlan.getEffectiveStatus();

    if (pjPlanStatus !== PjpPlanStatus.Completed) {
        // blocking in progress and error plans
        // case when user is directly coming from url
        return (
            <Dialog isOpen sticky onDismiss={onDismissPlan}>
                <DialogHeader>
                    <Heading level={4}>
                        {t(
                            'pjpCreate.incrementalPlan.planInProgressErrorTitle'
                        )}
                    </Heading>
                </DialogHeader>
                <DialogContent>
                    <Text fontSize={2}>
                        {t(
                            'pjpCreate.incrementalPlan.planInProgressErrorBody',
                            { planId: selectedPlan.id }
                        )}
                    </Text>
                </DialogContent>
                <DialogFooter>
                    <Button onClick={onDismissPlan}>
                        {t('translations:goBack')}
                    </Button>
                </DialogFooter>
            </Dialog>
        );
    }

    if (incrementalPlanType === null) {
        return (
            <IncrementalConfigSelector
                theme={theme}
                isOpen
                selectPlanType={selectFinalPlanType}
                onDismiss={onDismissPlan}
            />
        );
    }

    return (
        <IncrementalPlanProvider
            value={{
                incrementalPlanType,
                config,
                onOutletsNext,
                onRidersNext,
                onPlanCreate
            }}
        >
            <IncrementalPjpStepsContainer
                {...props}
                theme={theme}
                selectedPlan={selectedPlan}
            />
        </IncrementalPlanProvider>
    );
};

const IncrementalPlanWithPermissionCheck = (props: Props) => {
    const hasPermission = usePermissionMatch(RoleOperationEnum.CREATE_PJP);
    const { t } = useTranslation('entityManagement');

    if (!hasPermission) {
        return (
            <AccessDeniedDialog
                action={t('pjpCreate.incrementalPlan.incrementalPlan')}
                onDismiss={props.onDismiss}
            />
        );
    }
    return <IncrementalPjpPlan {...props} />;
};

export default IncrementalPlanWithPermissionCheck;
